import React, { useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'

import { useTOCContext } from './TOCContext'

import useActiveHash from 'hooks/useActiveHash'
import Clickable from 'components/Clickable'
import fixId from 'utils/fixId'
import theme from 'src/theme'
import CamelCaseWrap from 'components/CamelCaseWrap'

// prettier-ignore
const ItemText = styled(Clickable)`
	position: relative;
	padding-left: ${p => p.depth * 16 + 24}px;
	margin-bottom: 16px;

	color: var(--color-grey);

	${p => p.depth === 0 && `
		${theme.typography.scale.body2.regular}

		&.current, &:hover {
			${theme.typography.scale.body2.regular}
			color: var(--color-text);
		}
	`}

	${p => p.depth > 0 && `
		${theme.typography.scale.body2.regular}

		&.current, &:hover {
			${theme.typography.scale.body2.regular}
			color: var(--color-text);
		}
	`}

	line-height: 20px;
	min-height: 20px;


	&:before {
		content: '';
		height: 12px;
		width: 2px;
		left: 0px;
		top: 4px;
		background: var(--color-coral);
		position: absolute;
		opacity: 0;
	}
	${p => p.current && css`
		&:before {
			opacity: 1;
		}
	`}
`

const getId = (url = '') => url.replace('#', '') // `toc-${url.replace('#', '')}`
const getTo = (url = '') => {
	const parts = url.split('/')

	return parts[parts.length - 1]
}

const TableOfContents = ({ items, maxDepth = 4, internalDepth = 2, activeHash }) => {
	if (!items?.length) return null
	if (Number.isFinite(maxDepth) && internalDepth > maxDepth) return null
	const shouldRenderChildrenList = !Number.isFinite(maxDepth) || internalDepth + 1 <= maxDepth

	return (
		<ul>
			{items
				.filter(itm => {
					return Number.isFinite(maxDepth) ? itm.depth <= maxDepth : true
				})
				.map(itm => {
					const { title, items, depth } = itm

					const url = fixId(itm.url)

					return (
						<li key={url}>
							{title && (
								<ItemText
									to={getTo(url)}
									/* -2 cos we are starting at H2, so H2 which is top level will have depth 0, meaning no indent in TOC */
									depth={depth - 2}
									id={getId(url)}
									current={url === `#${activeHash}`}
									className={url === `#${activeHash}` ? 'current' : ''}
								>
									<CamelCaseWrap text={title} />
								</ItemText>
							)}
							{!!items?.length && shouldRenderChildrenList && (
								<TableOfContents
									items={items}
									maxDepth={maxDepth}
									internalDepth={internalDepth + 1}
									activeHash={activeHash}
								/>
							)}
						</li>
					)
				})}
		</ul>
	)
}

TableOfContents.propTypes = {
	depth: PropTypes.number,
	activeHash: PropTypes.string,
	maxDepth: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
	items: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			url: PropTypes.string,
			items: PropTypes.array,
		}),
	),
}

const urlToId = s => (s ? fixId(s.slice(1)) : undefined)

const getIds = ({ items = [], depth = 1, maxDepth }) =>
	items
		.reduce((acc, { url, items }) => {
			if (Number.isFinite(maxDepth) && depth >= maxDepth) return acc
			if (!items?.length && !url) return acc
			if (!items?.length) return [...acc, urlToId(url)]
			return [...acc, urlToId(url), ...getIds({ items, depth: depth + 1, maxDepth })]
		}, [])
		.filter(id => typeof id !== 'undefined')

const ActiveHashProvider = props => {
	const ids = useMemo(
		() => getIds({ items: props.items, depth: props.depth, maxDepth: props.maxDepth }),
		[props.items, props.depth, props.maxDepth],
	)
	const activeHash = useActiveHash(ids)

	useEffect(() => {
		if (!activeHash) return
		const el = document.getElementById(getId(activeHash))
		if (!el) return
	}, [activeHash])

	if (props.items?.length === 0) {
		return null
	}

	return <TableOfContents {...props} activeHash={activeHash} />
}

const TableOfContentsWithContext = props => {
	const { tocValue } = useTOCContext()
	return <ActiveHashProvider {...tocValue} {...props} />
}

export default TableOfContentsWithContext
